function throttle(callback) {
  let requestId = null, lastArgs;
  const later = (context) => () => {
    requestId = null;
    callback.apply(context, lastArgs);
  };
  const throttled = (...args) => {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(later(this));
    }
  };
  throttled.cancel = () => {
    cancelAnimationFrame(requestId);
    requestId = null;
  };
  return throttled;
}

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};

var ScrollProgress = class extends HTMLElement {
  connectedCallback() {
    this.scrolledElement.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));
    if (window.ResizeObserver) {
      new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
    }
  }
  get scrolledElement() {
    return this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute("observes"));
  }
  _updateScrollProgress() {
    const scrollLeft = document.dir === "ltr" ? this.scrolledElement.scrollLeft : Math.abs(this.scrolledElement.scrollLeft), advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
    this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
  }
};
if (!window.customElements.get("scroll-progress")) {
  window.customElements.define("scroll-progress", ScrollProgress);
}

var CarouselNavigation = class extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel navigation component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    this.items = Array.from(this.querySelectorAll("button"));
    this.items.forEach((button) => button.addEventListener("click", this.onButtonClicked.bind(this)));
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  onButtonClicked(event) {
    event.preventDefault();
    this.carousel.classList.remove("animate--slide-in")

    this.items.forEach(btn => {
      btn.setAttribute('aria-current', 'false');
    });

    event.target.setAttribute('aria-current', 'true');

    this.carousel.querySelectorAll('li[data-tab]').forEach(li => {
      if (li.getAttribute('data-tab') !== event.target.dataset.tab) {
        li.classList.add('hidden');
      } else {
        li.classList.remove('hidden');
      }
    });
    this.carousel.initPages();
    if (this.carousel.querySelector('scroll-progress')) {
      this.carousel.querySelector('scroll-progress')._updateScrollProgress()
    }
    this.carousel.classList.add("animate--slide-in")
  }
};
if (!window.customElements.get("carousel-navigation")) {
  window.customElements.define("carousel-navigation", CarouselNavigation);
}

var CountdownTimer = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this.isVisible = false;
  }
  connectedCallback() {
    this.flips = Array.from(this.querySelectorAll("countdown-timer-flip"));
    const expiresAt = this.getAttribute("expires-at");
    if (expiresAt !== "") {
      this.expirationDate = new Date(expiresAt);
      this.interval = setInterval(this.recalculateFlips.bind(this), 1e3);
    }
    // inView11(this, () => {
    //   this.isVisible = true;
    //   return () => this.isVisible = false;
    // }, { margin: "500px" });
  }
  disconnectedCallback() {
    clearInterval(this.interval);
  }
  get daysFlip() {
    return this.flips.find((flip) => flip.getAttribute("type") === "days");
  }
  get hoursFlip() {
    return this.flips.find((flip) => flip.getAttribute("type") === "hours");
  }
  get minutesFlip() {
    return this.flips.find((flip) => flip.getAttribute("type") === "minutes");
  }
  get secondsFlip() {
    return this.flips.find((flip) => flip.getAttribute("type") === "seconds");
  }

  recalculateFlips () {
    const dateNow = new Date();
    if (this.expirationDate < dateNow) {
      if (this.getAttribute("expiration-behavior") === "hide") {
        this.closest(".shopify-section").remove();
      } else {
        return clearInterval(this.interval);
      }
    }
    if (this.isVisible) {
      return;
    }
    let delta = Math.abs(this.expirationDate - dateNow) / 1e3;
    const days = Math.floor(delta / 86400);
    delta -= days * 86400;
    const hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;
    const minutes = Math.floor(delta / 60) % 60;
    delta -= minutes * 60;
    const seconds = Math.floor(delta % 60);
    this.daysFlip?.updateValue(days);
    this.hoursFlip?.updateValue(hours);
    this.minutesFlip?.updateValue(minutes);
    this.secondsFlip?.updateValue(seconds);
  };
};

var CountdownTimerFlip = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    let flipHtml = [...this.textContent].map(() => `<countdown-timer-flip-digit part="digit" ${this.hasAttribute("animate") ? "animate" : ""} style="display: inline-block">0</countdown-timer-flip-digit>`);
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(flipHtml.join("")));
  }
  updateValue(value) {
    this.textContent = Math.min(99, value).toString().padStart(2, "0");
    [...this.textContent].forEach((digit, index) => {
      this.shadowRoot.children[index].setAttribute("number", digit);
    });
  }
};

var CountdownTimerFlipDigit = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment("<div><slot></slot></div>"));
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.hasAttribute("animate")) {
      return this.textContent = newValue;
    }
    // await animate15(this.shadowRoot.firstElementChild, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-8px)"] }, { duration: 0.3, easing: [0.64, 0, 0.78, 0] }).finished;
    this.textContent = newValue;
    // animate15(this.shadowRoot.firstElementChild, { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0px)"] }, { duration: 0.3, easing: [0.22, 1, 0.36, 1] });
  }

  static get observedAttributes() {
    return ["number"];
  }
};
if (!window.customElements.get("countdown-timer")) {
  window.customElements.define("countdown-timer", CountdownTimer);
}
if (!window.customElements.get("countdown-timer-flip")) {
  window.customElements.define("countdown-timer-flip", CountdownTimerFlip);
}
if (!window.customElements.get("countdown-timer-flip-digit")) {
  window.customElements.define("countdown-timer-flip-digit", CountdownTimerFlipDigit);
}
